import { Injectable, inject } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { Router } from '@angular/router';
import { ProfileOption } from '../../models/menu.routes';
import { EventRegisterStorageService } from 'src/app/module/events/pages/event-main/event-register/services/event-register-storage.service';
import { ToastrService } from '../toastr/toastr.service';
import { ConfirmModalService } from '../confirm-modal/confirm-modal.service';

@Injectable({
  providedIn: 'root',
})
export class ProfileMenuService {
  private auth = inject(AuthService);
  private router = inject(Router);
  private eventRegisterStorageService = inject(EventRegisterStorageService);
  private toastr = inject(ToastrService);
  private confirmModal = inject(ConfirmModalService);


  getProfileOptions(): ProfileOption[] {
    return this.getRoleOptions().map((group) => ({
      ...group,
      items: group.items?.map((item) => ({
        ...item,
        styleClass:
          item.label === 'Suporte'
            ? 'profile-menu-item--support'
            : item.label === 'Sair'
              ? 'profile-menu-item--logout'
              : undefined,
      })),
    }));
  }

  getSidebarOptions(): ProfileOption[] {
    const roleOptions = this.getRoleOptions();

    if (this.auth.isCustomer()) {
      return [
        {
          label: 'Perfil',
          items: this.selectItems(roleOptions, ['Dados']),
        },
        {
          label: 'Ingressos',
          items: this.selectItems(roleOptions, ['Meus Ingressos', 'Histórico']),
        },
      ];
    }

    if (this.auth.isProducer()) {
      return [
        {
          label: 'Perfil',
          items: this.selectItems(roleOptions, ['Dados', 'Colaboradores']),
        },
        this.selectGroup(roleOptions, 'Eventos'),
        this.selectGroup(roleOptions, 'Formulários'),
      ].filter((group): group is ProfileOption => Boolean(group));
    }

    if (this.auth.isAdmin()) {
      return [
        {
          label: 'Perfil',
          items: this.selectItems(roleOptions, ['Dados']),
        },
        {
          label: 'Gestão',
          items: this.selectItems(roleOptions, ['Usuários', 'Colaboradores']),
        },
        this.selectGroup(roleOptions, 'Eventos'),
        this.selectGroup(roleOptions, 'Formulários'),
        this.selectGroup(roleOptions, 'Relatórios'),
      ].filter((group): group is ProfileOption => Boolean(group));
    }

    return [];
  }

  private getRoleOptions(): ProfileOption[] {
    if (this.auth.isCustomer()) {
      return this.getProfileUser();
    }
    if (this.auth.isProducer()) {
      return this.getProfileProducer();
    }
    if (this.auth.isAdmin()) {
      return this.getProfileAdmin();
    }
    return [];
  }

  private selectItems(groups: ProfileOption[], labels: string[]): ProfileOption[] {
    const items = groups.flatMap((group) => group.items ?? []);

    return labels
      .map((label) => items.find((item) => item.label === label))
      .filter((item): item is ProfileOption => Boolean(item))
      .map((item) => ({ ...item }));
  }

  private selectGroup(groups: ProfileOption[], label: string): ProfileOption | null {
    const group = groups.find((option) => option.label === label);
    return group ? { ...group, items: [...(group.items ?? [])] } : null;
  }

  private navigateToProfile(tab: number) {
    // Se já estamos na rota /perfil, forçar a navegação
    if (this.router.url.startsWith('/perfil')) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/perfil'], { queryParams: { tab } });
      });
    } else {
      this.router.navigate(['/perfil'], { queryParams: { tab } });
    }
  }

  private getProfileUser(): ProfileOption[] {
    return [
      {
        label: 'Perfil',
        items: [
          {
            label: 'Dados',
            icon: 'pi pi-user',
            routerLink: ['/perfil'],
            queryParams: { tab: 0 },
          },
          {
            label: 'Meus Ingressos',
            icon: 'pi pi-ticket',
            routerLink: ['/perfil'],
            queryParams: { tab: 2 },
          },
          {
            label: 'Histórico',
            icon: 'pi pi-history',
            routerLink: ['/perfil'],
            queryParams: { tab: 3 },
          },
          {
            label: 'Suporte',
            icon: 'pi pi-whatsapp',
            routerLink: ['/configuracoes/suporte'],
          },
        ],
      },
      {
        label: '',
        items: [
          {
            label: 'Sair',
            icon: 'pi pi-sign-out',
            command: () => this.logout(),
          },
        ],
      },
    ];
  }

  private getProfileProducer(): ProfileOption[] {
    return [
      {
        label: 'Perfil',
        items: [
          {
            label: 'Dados',
            icon: 'pi pi-user',
            routerLink: ['/perfil'],
            queryParams: { tab: 0 },
          },
          {
            label: 'Colaboradores',
            icon: 'pi pi-users',
            routerLink: ['/colaborador'],
          },
          {
            label: 'Suporte',
            icon: 'pi pi-whatsapp',
            routerLink: ['/configuracoes/suporte'],
          },
        ],
      },
      {
        label: 'Eventos',
        items: [
          {
            label: 'Meus Eventos',
            icon: 'pi pi-ticket',
            routerLink: ['/eventos'],
            exact: true,
          },
          {
            label: 'Criar Evento',
            icon: 'pi pi-plus-circle',
            activeRoute: '/eventos/registrar',
            command: () => this.createEvent(),
          },
        ],
      },
      {
        label: 'Formulários',
        items: [
          {
            label: 'Meus Formulários',
            icon: 'pi pi-file-edit',
            routerLink: ['/formularios'],
            exact: true,
          },
          {
            label: 'Criar Formulário',
            icon: 'pi pi-plus-circle',
            activeRoute: '/formularios/novo',
            command: () => this.createForm(),
          },
        ],
      },
      {
        label: '',
        items: [
          {
            label: 'Sair',
            icon: 'pi pi-sign-out',
            command: () => this.logout(),
          },
        ],
      },
    ];
  }

  private getProfileAdmin(): ProfileOption[] {
    return [
      {
        label: 'Perfil',
        items: [
          {
            label: 'Dados',
            icon: 'pi pi-user',
            routerLink: ['/perfil'],
            queryParams: { tab: 0 },
          },
          {
            label: 'Usuários',
            icon: 'pi pi-users',
            routerLink: ['/usuarios'],
          },
          {
            label: 'Colaboradores',
            icon: 'pi pi-users',
            routerLink: ['/colaborador'],
            exact: true,
          },
        ],
      },
      {
        label: 'Eventos',
        items: [
          {
            label: 'Meus Eventos',
            icon: 'pi pi-ticket',
            routerLink: ['/eventos'],
            exact: true,
          },
          {
            label: 'Criar Evento',
            icon: 'pi pi-plus-circle',
            activeRoute: '/eventos/registrar',
            command: () => this.createEvent(),
          },
        ],
      },
      {
        label: 'Formulários',
        items: [
          {
            label: 'Meus Formulários',
            icon: 'pi pi-file-edit',
            routerLink: ['/formularios'],
            exact: true,
          },
          {
            label: 'Criar Formulário',
            icon: 'pi pi-plus-circle',
            activeRoute: '/formularios/novo',
            command: () => this.createForm(),
          },
        ],
      },
      {
        label: 'Relatórios',
        items: [
          {
            label: 'Conciliação MP',
            icon: 'pi pi-wallet',
            routerLink: ['/relatorios-financeiros'],
            exact: true,
          },
        ],
      },
      {
        label: '',
        items: [
          {
            label: 'Sair',
            icon: 'pi pi-sign-out',
            command: () => this.logout(),
          },
        ],
      },
    ];
  }

  logout() {
    this.confirmModal.confirm(
      'Sair',
      'Você está prestes a desconectar. Deseja continuar?',
      () => {
        this.auth.removeToken();
        this.eventRegisterStorageService.deleteEventRegisterInfo();
        this.router.navigate(['/']);
        this.toastr.success('Sessão encerrada.');
      },
      () => null,
      'Confirmar',
      'Cancelar',
      'warning'
    );
  }

  private async createEvent(): Promise<void> {
    this.eventRegisterStorageService.deleteEventRegisterInfo();

    if (this.router.url.split(/[?#]/)[0].startsWith('/eventos/registrar')) {
      await this.router.navigateByUrl('/eventos/meus-eventos', { skipLocationChange: true });
    }

    await this.router.navigateByUrl('/eventos/registrar/sobre');
  }

  private createForm() {
    this.router.navigate(['/formularios', 'novo']);
  }
}
